import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLang } from "@/LangContext";
import { scrollToId } from "@/lib/scroll";

const LANGS = ["es", "en", "fr", "pt"];

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "ecosistema", label: t.nav.ecosystem, testid: "nav-link-ecosystem" },
    { id: "areas", label: t.nav.areas, testid: "nav-link-areas" },
    { id: "naming", label: t.nav.naming, testid: "nav-link-naming" },
    { id: "subasta", label: t.nav.auction, testid: "nav-link-auction" },
    { id: "contacto", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
        scrolled || open
          ? "border-white/10 bg-[#0A0C10]/85 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          data-testid="brand-logo"
          onClick={() => go("inicio")}
          className="flex items-center gap-3 text-left"
        >
          <span className="relative block h-10 w-10 shrink-0">
            <span className="absolute inset-1 rotate-45 border-2 border-amber-400 transition-transform duration-500 group-hover:rotate-90" />
            <span className="absolute inset-0 flex items-center justify-center font-display text-lg font-bold text-amber-400">
              D
            </span>
          </span>
          <span>
            <span className="block font-display text-sm font-bold uppercase leading-tight tracking-[0.16em] text-white">
              Fundación Deportiva Disimil
            </span>
            <span className="block font-code text-[10px] uppercase tracking-[0.32em] text-amber-400/90">
              Complejo Montalbán
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="font-code text-[11px] uppercase tracking-[0.22em] text-slate-300 transition-colors hover:text-amber-400"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="hidden items-center border border-white/10 sm:flex"
            data-testid="language-switcher-button"
          >
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`language-option-${l}`}
                onClick={() => setLang(l)}
                className={`px-2.5 py-1.5 font-code text-[11px] uppercase tracking-widest transition-colors ${
                  lang === l
                    ? "bg-amber-400 font-semibold text-[#0A0C10]"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <button
            data-testid="nav-dossier-button"
            onClick={() => go("contacto")}
            className="hidden bg-amber-400 px-5 py-2.5 font-display text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors hover:bg-amber-300 md:block"
          >
            {t.nav.cta}
          </button>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center border border-white/10 text-white lg:hidden"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="border-t border-white/10 bg-[#0A0C10]/95 px-6 py-6 backdrop-blur-xl lg:hidden"
        >
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-${l.testid}`}
                onClick={() => go(l.id)}
                className="text-left font-display text-2xl font-bold uppercase tracking-wide text-white"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-nav-dossier-button"
              onClick={() => go("contacto")}
              className="mt-2 bg-amber-400 px-5 py-3 text-left font-display text-base font-bold uppercase tracking-[0.14em] text-[#0A0C10]"
            >
              {t.nav.cta}
            </button>
            <div className="mt-2 flex gap-1 border border-white/10 self-start">
              {LANGS.map((l) => (
                <button
                  key={l}
                  data-testid={`mobile-language-option-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-3 py-2 font-code text-xs uppercase tracking-widest ${
                    lang === l
                      ? "bg-amber-400 font-semibold text-[#0A0C10]"
                      : "text-slate-400"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
